import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { appService } from '../../services/appService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';
import SearchableSelect from '../../components/common/SearchableSelect';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';

const LIMIT = 20;

const STATUS_OPTIONS = [
   { id: '', name: 'All Statuses' },
   { id: 'DRAFT', name: 'Draft' },
   { id: 'PUBLISHED', name: 'Published' },
   { id: 'ARCHIVED', name: 'Archived' },
];

const statusBadge = (status) => {
   const map = {
      DRAFT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      PUBLISHED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      ARCHIVED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
   };
   return (
      <span
         className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}
      >
         {status}
      </span>
   );
};

const Apps = () => {
   const navigate = useNavigate();
   const [apps, setApps] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [search, setSearch] = useState('');
   const [statusFilter, setStatusFilter] = useState('');
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);

   const fetchApps = useCallback(async () => {
      try {
         setLoading(true);
         const res = await appService.getAll({
            page,
            limit: LIMIT,
            status: statusFilter || undefined,
            search: search.trim() || undefined,
         });
         setApps(res.data);
         setTotalPages(res.pagination.totalPages);
         setTotal(res.pagination.total);
      } catch {
         toast.error('Failed to fetch apps');
      } finally {
         setLoading(false);
      }
   }, [page, statusFilter, search]);

   useEffect(() => {
      fetchApps();
   }, [fetchApps]);

   const handleSearchChange = (e) => {
      setSearch(e.target.value);
      setPage(1);
   };

   const handleStatusChange = (value) => {
      setStatusFilter(value);
      setPage(1);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await appService.delete(deleteTarget.id);
         toast.success('App deleted successfully');
         setDeleteTarget(null);
         fetchApps();
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to delete app');
      } finally {
         setDeleting(false);
      }
   };

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Apps</h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                     Manage side-project apps and their downloadable packages
                  </p>
               </div>
               <Button onClick={() => navigate('/admin/apps/new')}>New App</Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
               <div className="flex-1 max-w-sm">
                  <Input
                     value={search}
                     onChange={handleSearchChange}
                     placeholder="Search by name or tagline…"
                  />
               </div>
               <div className="w-48">
                  <SearchableSelect
                     label=""
                     searchable={false}
                     options={STATUS_OPTIONS}
                     placeholder="Select Status"
                     value={statusFilter}
                     onChange={handleStatusChange}
                     className="!mb-0"
                  />
               </div>
            </div>

            {loading ? (
               <Loader />
            ) : (
               <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Name
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              In Projects
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Created
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {apps.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="5"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No apps found
                              </td>
                           </tr>
                        ) : (
                           apps.map((app) => (
                              <tr
                                 key={app.id}
                                 onClick={() => navigate(`/admin/apps/${app.id}`)}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                              >
                                 <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                       {app.name}
                                    </div>
                                    {app.tagline && (
                                       <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xs truncate">
                                          {app.tagline}
                                       </div>
                                    )}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    {statusBadge(app.status)}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                       {app.showInProjects ? 'Yes' : 'No'}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                       <DateTimeDisplay dateStr={app.createdAt} />
                                    </span>
                                 </td>
                                 <td
                                    className="px-6 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu
                                       items={[
                                          {
                                             label: 'Edit',
                                             onClick: () => navigate(`/admin/apps/${app.id}`),
                                          },
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(app),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            )}

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete App"
            message={`Are you sure you want to delete "${deleteTarget?.name}"? This also deletes all of its releases and package files. This action cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Apps;
